import React, { memo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import PublishIcon from "@mui/icons-material/Publish";
import { TimelineGridRow } from "../timelinePlanner/timelinePlannerGrid";
import { DateActions } from "./DateActions";
import { PlannedWorkShiftDto, PlannedWorkShiftState } from "../../models/plannedWorkShift/plannedWorkShift";
import { Route, useParams } from "react-router-dom";
import { WorkShiftTimelineCell } from "./WorkShiftTimelineCell";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap, Chip, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePublishShifts } from "../../hooks/workShiftPlanning/usePublishShifts";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { WorkShiftPlanningParams } from "./WorkShiftPlanningRouter";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { toggleRowExpanded } from "../../store/workShiftPlanningSlice";
import { AppUtils } from "../../models/common/appUtils";

interface WorkShiftTimelineRowProps {
    shifts: PlannedWorkShiftDto[];
    selectedShiftIds?: string[];
    targetId: string;
    label: string;
    totalActualMinutes?: number;
    totalPlannedMinutes?: number;
    projectedActualMinutes?: number;
    Icon: OverridableComponent<SvgIconTypeMap<any, "svg">>;
    onShiftClick?: (shiftId: string, event: React.MouseEvent, shift: PlannedWorkShiftDto) => void;
    selectedCell?: { day: string; targetId: string } | null;
    onCellSelect?: (cell: { day: string; targetId: string }, event?: React.MouseEvent) => void;
}

const WorkShiftChipTooltip = ({
    title,
    color,
    value,
    showDash = false
}: {
    title: string;
    color: "success" | "primary" | "warning";
    value?: number;
    showDash?: boolean;
}) => (
    <Tooltip title={title}>
        <Chip
            color={color}
            size="small"
            sx={{ minWidth: 50 }}
            label={
                value
                    ? AppUtils.getDurationStrByDurationMinShort(value, true)
                    : showDash ? <Typography>-</Typography> : ""
            }
        />
    </Tooltip>
);

export const WorkShiftTimelineRow = memo(
    ({
        shifts = [],
        targetId,
        label,
        totalActualMinutes,
        totalPlannedMinutes,
        projectedActualMinutes,
        Icon,
        selectedShiftIds = [],
        onShiftClick,
        onCellSelect,
        selectedCell
    }: WorkShiftTimelineRowProps) => {
        const { t } = useTranslation();
        const { publishEntityShifts, saving } = usePublishShifts();
        const { tab } = useParams<WorkShiftPlanningParams>();
        const dispatch = useAppDispatch();

        const cardViewMode = useAppSelector(
            (state) => state.workShiftPlanning.cardViewMode
        );
        const rowExpanded = useAppSelector(
            (state) => state.workShiftPlanning.rowExpanded
        );
        const isRowExpanded =
            rowExpanded?.[targetId ?? "null"] ??
            (cardViewMode === "expanded");

        const allShifts = useAppSelector(
            (state) => state.workShiftPlanning.plannedWorkShifts
        );

        const hasPlannedShifts = shifts?.some(
            (s) => s.state === PlannedWorkShiftState.InPlanning
        );

        const handlePublishEntityShifts = () => {
            if (targetId && targetId !== "null") {
                void publishEntityShifts(allShifts, targetId, tab);
            }
        };

        const showPublishButton = targetId !== "null" && hasPlannedShifts;

        return (
            <TimelineGridRow
                renderColumnHeader={(date: string) => (
                    <Route
                        render={(props) => (
                            <DateActions
                                {...props}
                                date={date}
                                targetId={targetId}
                                selectedCell={selectedCell}
                                onCellSelect={onCellSelect}
                            />
                        )}
                    />
                )}
                cellSelection={
                    onCellSelect ? {
                        targetId,
                        selectedCell,
                        onCellSelect
                    } : undefined}
                infoColumn={
                    <Box sx={{ fontSize: "small", pt: 1, pb: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <div className="bold flex align-items-center overflow-break">
                                <Icon
                                    sx={{ mr: 1, color: "primary.dark" }}
                                    fontSize="small"
                                />
                                <span>{label}</span>
                            </div>

                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                {showPublishButton ? (
                                    <Tooltip title={t("workShiftPlanning.publishEntityShifts")}>
                                        <IconButton
                                            size="small"
                                            disabled={saving}
                                            onClick={handlePublishEntityShifts}
                                            color="primary"
                                        >
                                            <PublishIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                <Tooltip title={
                                    isRowExpanded
                                        ? t("workShiftPlanning.compact.collapseRow")
                                        : t("workShiftPlanning.compact.expandRow")
                                }
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => dispatch(toggleRowExpanded(targetId ?? "null"))}
                                        color="primary"
                                    >
                                        {isRowExpanded ? (
                                            <ExpandLessIcon fontSize="small" />
                                        ) : (
                                            <ExpandMoreIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                        {(totalPlannedMinutes !== undefined || totalActualMinutes !== undefined) && (
                        <Stack direction="row" gap={1}>
                            {totalActualMinutes !== undefined && (
                                <WorkShiftChipTooltip
                                    title={t("workShiftPlanning.actualWorkhours")}
                                    color="success"
                                    value={totalActualMinutes}
                                    showDash={true}
                                />
                            )}
                            {totalPlannedMinutes !== undefined && (
                                <WorkShiftChipTooltip
                                    title={t("workShiftPlanning.plannedWorkhours")}
                                    color="primary"
                                    value={totalPlannedMinutes}
                                    showDash={true}
                                />
                            )}
                            {projectedActualMinutes !== undefined && (
                                <WorkShiftChipTooltip
                                    title={t("workShiftPlanning.projectedActualWorkhours")}
                                    color="warning"
                                    value={projectedActualMinutes}
                                />
                            )}
                        </Stack>
                        )}
                    </Box>
                }
            >
                {shifts?.map((shift) => (
                    <WorkShiftTimelineCell
                        shift={shift}
                        key={shift.id}
                        isSelected={selectedShiftIds.includes(shift.id)}
                        onClick={(e) => onShiftClick?.(shift.id, e, shift)}
                        isCompact={!isRowExpanded}
                    />
                ))}
            </TimelineGridRow>
        );
    }
);
